import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useUserAuth } from '../context/UserAuthContext';

const HomeContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const WelcomeSection = styled.section`
  background-color: var(--card-bg);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--border-color);
`;

const WelcomeTitle = styled.h1`
  font-size: 2rem;
  color: var(--text-color);
  margin-bottom: 1rem;
`;

const WelcomeText = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const Card = styled(Link)`
  background-color: var(--card-bg);
  border-radius: 8px;
  padding: 1.5rem;
  text-decoration: none;
  color: var(--text-color);
  border: 1px solid var(--border-color);
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
    border-color: var(--primary-color);
  }
`;

const CardIcon = styled.div`
  font-size: 2rem;
  color: var(--primary-color);
  margin-bottom: 1rem;
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 0.75rem;
`;

const CardDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.9375rem;
  line-height: 1.5;
  margin-bottom: 1.5rem;
  flex-grow: 1;
`;

const CardAction = styled.div`
  color: var(--primary-color);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::after {
    content: '→';
    transition: transform 0.2s ease;
  }
  
  ${Card}:hover &::after {
    transform: translateX(4px);
  }
`;

const Home = () => {
  const { user } = useUserAuth();
  
  // Get user's first name or default to a generic greeting
  const userName = user?.name?.split(' ')[0] || 'there';
  
  return (
    <HomeContainer>
      <WelcomeSection>
        <WelcomeTitle>Welcome back, {userName}!</WelcomeTitle>
        <WelcomeText>
          Here's what's happening with your strawberry farm today. Check your zones, 
          get recommendations, and stay on top of your farming tasks.
        </WelcomeText>
      </WelcomeSection>
      
      <CardsGrid>
        <Card to="/zones">
          <CardIcon>🌱</CardIcon>
          <CardTitle>Growing Zones</CardTitle>
          <CardDescription>
            Monitor and manage your strawberry growing zones. Check soil conditions, 
            moisture levels, and plant health status.
          </CardDescription>
          <CardAction>View Zones</CardAction>
        </Card>
        
        <Card to="/ask-agent">
          <CardIcon>🤖</CardIcon>
          <CardTitle>Ask the Agent</CardTitle>
          <CardDescription>
            Get AI-powered advice for your strawberry farming challenges. Ask about 
            plant health, pest control, and growing conditions.
          </CardDescription>
          <CardAction>Ask a Question</CardAction>
        </Card>
        
        <Card to="/reports">
          <CardIcon>📊</CardIcon>
          <CardTitle>Reports & Trends</CardTitle>
          <CardDescription>
            View detailed reports and analyze trends in your farm's performance, 
            yield predictions, and environmental conditions.
          </CardDescription>
          <CardAction>View Reports</CardAction>
        </Card>
        
        <Card to="/tasks">
          <CardIcon>✅</CardIcon>
          <CardTitle>Tasks</CardTitle>
          <CardDescription>
            Manage your daily, weekly, and seasonal farming tasks. Never miss an 
            important maintenance activity.
          </CardDescription>
          <CardAction>View Tasks</CardAction>
        </Card>
      </CardsGrid>
    </HomeContainer>
  );
};

export default Home;
